/**
 * 使Ant Design Vue的Table组件支持表头sticky（页面滚动时表头固定）
 * 单元格宽度必须固定，表格需设置 :scroll="{ x: 'max-content' }" 以生成正确的 DOM 结构
 *
 * 注意：状态必须挂在指令元素上，不能使用模块级变量，否则多表格/路由切换会互相污染，
 * 且表格重渲染后 originEl 失效会导致 getBoundingClientRect 为 0，误触发表头吸顶。
 */
import throttle from 'lodash/throttle'

const originSelector = '.ant-table-content'
const scrollSelector = '.ant-table-scroll .ant-table-body, .ant-table-body'
const toRemoveSelector = '.ant-table-tbody,.ant-table-placeholder'

const getBindingConfig = (binding) => {
  const params = binding.value || {}
  const fixedTop = params.fixedTop || 0
  const zIndex = params.zIndex || 1000
  const bgColor = params.bgColor || '#fff'
  const { disabled } = params
  const { scrollContainerId } = params
  return { fixedTop, zIndex, disabled, scrollContainerId, bgColor }
}

const getScrollContainer = (scrollContainerId) => {
  return (scrollContainerId && document.getElementById(scrollContainerId)) || window
}

const createInstance = (el, binding) => {
  const config = getBindingConfig(binding)
  const instance = {
    el,
    config,
    container: null,
    originEl: null,
    stickyHeader: null,
    bodyScroller: null,
    active: false,
    listenAction: null,
    resizeStickyHeader: null,
    setScrollX: null,
  }

  instance.resizeStickyHeader = throttle(() => {
    if (!instance.stickyHeader || !instance.originEl) return
    // 元素已脱离文档时宽高为 0，忽略以免把吸顶表头宽度清成 0
    const width = instance.originEl.getBoundingClientRect().width
    if (width > 0) {
      instance.stickyHeader.style.width = `${width}px`
    }
  }, 50)

  instance.setScrollX = throttle(() => {
    if (!instance.stickyHeader || !instance.originEl) return
    const stickyHeaderScroller = instance.stickyHeader.querySelector(scrollSelector)
    const originScroller = instance.originEl.querySelector(scrollSelector)
    if (stickyHeaderScroller && originScroller) {
      stickyHeaderScroller.scrollLeft = originScroller.scrollLeft
    }
  }, 16)

  instance.listenAction = throttle(() => {
    syncOriginEl(instance)
    if (!instance.originEl) {
      reset(instance)
      return
    }
    const rect = instance.originEl.getBoundingClientRect()
    const { fixedTop } = instance.config
    // 仅当表头滚过吸顶线、且表格主体仍在视口内时展示；宽高为 0 说明 DOM 失效，不吸顶
    const shouldSticky =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.top <= fixedTop &&
      rect.bottom > fixedTop + 40

    if (shouldSticky) {
      sticky(instance)
    } else {
      reset(instance)
    }
  }, 50)

  return instance
}

const syncOriginEl = (instance) => {
  const nextOrigin = instance.el.querySelector(originSelector)
  if (!nextOrigin) {
    instance.originEl = null
    return
  }
  if (nextOrigin !== instance.originEl) {
    detachBodyScroll(instance)
    instance.originEl = nextOrigin
    attachBodyScroll(instance)
    if (instance.stickyHeader) {
      adaptStickyHeader(instance)
    }
  }
}

const attachBodyScroll = (instance) => {
  detachBodyScroll(instance)
  if (!instance.originEl) return
  const scroller = instance.originEl.querySelector(scrollSelector)
  instance.bodyScroller = scroller
  scroller && scroller.addEventListener('scroll', instance.setScrollX)
}

const detachBodyScroll = (instance) => {
  instance.bodyScroller && instance.bodyScroller.removeEventListener('scroll', instance.setScrollX)
  instance.bodyScroller = null
}

const ensureStickyHeader = (instance) => {
  if (instance.stickyHeader) return
  if (!instance.originEl) return
  instance.stickyHeader = instance.originEl.cloneNode(true)
  const { fixedTop, zIndex, bgColor } = instance.config
  instance.stickyHeader.classList.add('v-table-sticky-header')
  instance.stickyHeader.style.cssText +=
    `;position:fixed;top:${fixedTop}px;z-index:${zIndex};background-color:${bgColor};pointer-events:none;display:none;`
  adaptStickyHeader(instance)
}

const adaptStickyHeader = (instance) => {
  if (!instance.stickyHeader || !instance.originEl) return
  instance.stickyHeader.innerHTML = instance.originEl.innerHTML
  const stickyScroller = instance.stickyHeader.querySelector(scrollSelector)
  if (stickyScroller) {
    stickyScroller.style.overflowX = 'hidden'
  }
  Array.from(instance.stickyHeader.querySelectorAll(toRemoveSelector)).forEach((node) => {
    node.parentNode && node.parentNode.removeChild(node)
  })
  instance.resizeStickyHeader()
  instance.setScrollX()
}

const sticky = (instance) => {
  ensureStickyHeader(instance)
  if (!instance.stickyHeader || !instance.originEl) return
  if (!instance.active) {
    instance.originEl.insertAdjacentElement('afterend', instance.stickyHeader)
    instance.active = true
  }
  instance.stickyHeader.style.display = ''
  instance.setScrollX()
  instance.resizeStickyHeader()
}

const reset = (instance) => {
  if (!instance.stickyHeader) return
  if (instance.active) {
    instance.stickyHeader.parentNode && instance.stickyHeader.parentNode.removeChild(instance.stickyHeader)
    instance.active = false
  }
  instance.stickyHeader.style.display = 'none'
}

const watch = (instance) => {
  instance.container = getScrollContainer(instance.config.scrollContainerId)
  instance.container.addEventListener('scroll', instance.listenAction, { passive: true })
  window.addEventListener('resize', instance.resizeStickyHeader, { passive: true })
  attachBodyScroll(instance)
}

const unwatch = (instance, { cancelThrottle = false } = {}) => {
  if (instance.container) {
    instance.container.removeEventListener('scroll', instance.listenAction)
  }
  window.removeEventListener('resize', instance.resizeStickyHeader)
  detachBodyScroll(instance)
  instance.container = null
  if (cancelThrottle) {
    if (instance.listenAction && instance.listenAction.cancel) instance.listenAction.cancel()
    if (instance.resizeStickyHeader && instance.resizeStickyHeader.cancel) instance.resizeStickyHeader.cancel()
    if (instance.setScrollX && instance.setScrollX.cancel) instance.setScrollX.cancel()
  }
}

const destroy = (instance) => {
  if (!instance) return
  unwatch(instance, { cancelThrottle: true })
  reset(instance)
  instance.stickyHeader = null
  instance.originEl = null
}

const applyStickyStyle = (instance) => {
  if (!instance.stickyHeader) return
  const { fixedTop, zIndex, bgColor } = instance.config
  instance.stickyHeader.style.top = `${fixedTop}px`
  instance.stickyHeader.style.zIndex = zIndex
  instance.stickyHeader.style.backgroundColor = bgColor
}

export default {
  bind(el, binding) {
    const instance = createInstance(el, binding)
    el.__tableSticky = instance

    if (instance.config.disabled) return

    syncOriginEl(instance)
    ensureStickyHeader(instance)
    watch(instance)
    // 初始化时按当前滚动位置判断一次，避免残留吸顶
    instance.listenAction()
  },

  unbind(el) {
    destroy(el.__tableSticky)
    el.__tableSticky = null
  },

  update(el, binding) {
    let instance = el.__tableSticky
    if (!instance) {
      this.bind(el, binding)
      return
    }

    const nextConfig = getBindingConfig(binding)
    const wasDisabled = instance.config.disabled
    const containerChanged = nextConfig.scrollContainerId !== instance.config.scrollContainerId
    instance.config = nextConfig

    if (nextConfig.disabled) {
      unwatch(instance)
      reset(instance)
      return
    }

    // 从 disabled 恢复，或滚动容器变化时重新绑定监听
    if (wasDisabled || !instance.container || containerChanged) {
      unwatch(instance)
      syncOriginEl(instance)
      ensureStickyHeader(instance)
      applyStickyStyle(instance)
      watch(instance)
    } else {
      syncOriginEl(instance)
      ensureStickyHeader(instance)
      adaptStickyHeader(instance)
      applyStickyStyle(instance)
    }

    instance.listenAction()
  },
}
